/**
 * AdminReports — unified admin desk for every technical report the
 * business issues or receives:
 *   • Certificates written via /admin/certificates (electrical, EICR,
 *     PAT). Read-only here — the certificates page is still where
 *     they're created and edited.
 *   • EICR reports composed in the EICR editor.
 *   • Uploaded PDFs the admin drops in (e.g. a hand-written report
 *     from a subcontractor). Fully lifecycle-managed here.
 *
 * Sort: click any header (Customer, Site, Type, Issued, Next
 * inspection, Engineer). Default = Issued ↓ so newest is on top.
 * Filter pills scope to a single source when the admin wants to
 * focus on just uploaded PDFs.
 *
 * Upload dialog:
 *   - Multipart POST to /api/admin/reports/upload. Bytes stored
 *     inline in MongoDB so they survive pod redeploys.
 *   - Customer picker + optional site picker (auto-fills the
 *     customer when the site is chosen first).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Eye, Trash2, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  getAdminReports, uploadAdminReport, deleteAdminReport,
  adminReportFileUrl, getUsers, getSites, getAdminReportTypes,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useSortableList } from '../../hooks/useSortableList';
import { SortHeader, fmtDate } from '../../components/SortHeader';

const COMPARATORS = {
  kind:       (r) => (r.kind || '').toLowerCase(),
  customer:   (r) => (r.customerName || '').toLowerCase(),
  site:       (r) => (r.siteName || '').toLowerCase(),
  title:      (r) => (r.title || '').toLowerCase(),
  engineer:   (r) => (r.engineer || '').toLowerCase(),
  issuedAt:   (r) => r.issuedAt || '',
  nextInsp:   (r) => r.nextInspectionDate || '',
};

const SOURCE_LABELS = {
  certificate: 'Certificate',
  eicr: 'EICR Report',
  upload: 'Uploaded',
};

const AdminReports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('all');   // 'all' | 'certificate' | 'eicr' | 'upload'
  const [customerFilter, setCustomerFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = () => {
    setLoading(true);
    getAdminReports()
      .then(setRows)
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load reports'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  // Derive the customer + type dropdown option lists live from the
  // report rows themselves — no need for a separate `/customers`
  // fetch, keeps the filters honest (only shows values that actually
  // appear on a report).
  const customerOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      if (r.customerId && !seen.has(r.customerId)) {
        seen.set(r.customerId, r.customerName || r.customerId);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const typeOptions = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.kind) set.add(r.kind);
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(
    () => rows.filter(r => (
      (source === 'all' || r.source === source)
      && (customerFilter === 'all' || r.customerId === customerFilter)
      && (typeFilter === 'all' || r.kind === typeFilter)
    )),
    [rows, source, customerFilter, typeFilter],
  );

  const { sorted, sort, onSort } = useSortableList(filtered, COMPARATORS, {
    initialKey: 'issuedAt',
    initialDir: 'desc',
    defaultDescKeys: ['issuedAt', 'nextInsp'],
  });

  const counts = useMemo(() => ({
    all: rows.length,
    certificate: rows.filter(r => r.source === 'certificate').length,
    eicr: rows.filter(r => r.source === 'eicr').length,
    upload: rows.filter(r => r.source === 'upload').length,
  }), [rows]);

  const handleView = (r) => {
    if (r.source === 'upload') {
      window.open(adminReportFileUrl(r.id), '_blank', 'noopener,noreferrer');
    } else if (r.viewPath) {
      navigate(r.viewPath);
    }
  };

  const handleDelete = async (r) => {
    if (r.source !== 'upload') {
      toast.error('Certificates and EICR reports must be deleted from their own page.');
      return;
    }
    if (!window.confirm(`Delete uploaded report "${r.title}"? This cannot be undone.`)) return;
    try {
      await deleteAdminReport(r.id);
      setRows(prev => prev.filter(x => x.id !== r.id));
      toast.success('Report deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-reports-page">
      <PageHeader
        title="Reports"
        description="Every technical report we've issued — certificates, EICRs, and uploaded PDFs — in one place."
        actions={
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="upload-report-btn"
          >
            <Upload className="h-4 w-4 mr-2" /> Upload report
          </Button>
        }
      />

      {/* Source filter pills */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'all', label: 'All' },
          { key: 'certificate', label: 'Certificates' },
          { key: 'eicr', label: 'EICR Reports' },
          { key: 'upload', label: 'Uploaded' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSource(t.key)}
            data-testid={`source-filter-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              source === t.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
            }`}
          >
            {t.label} <span className="opacity-70">({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Customer + Type dropdown filters — layered on top of the
          source pills so the admin can drill "Uploaded certs, for
          Dave Tinsley, of type EICR Section K" in three clicks. */}
      <div className="flex flex-wrap gap-3">
        <div className="min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Customer
          </label>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="reports-customer-filter" className="h-9">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customerOptions.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Type
          </label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="reports-type-filter" className="h-9">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeOptions.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(customerFilter !== 'all' || typeFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setCustomerFilter('all'); setTypeFilter('all'); }}
            className="self-end mb-0.5 text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="reports-filter-reset"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading reports…</div>
        ) : sorted.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No reports found.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium text-slate-600">Ref</th>
                <SortHeader label="Type" sortKey="kind" sort={sort} onSort={onSort} />
                <SortHeader label="Customer" sortKey="customer" sort={sort} onSort={onSort} />
                <SortHeader label="Site" sortKey="site" sort={sort} onSort={onSort} />
                <SortHeader label="Engineer" sortKey="engineer" sort={sort} onSort={onSort} />
                <SortHeader label="Issued" sortKey="issuedAt" sort={sort} onSort={onSort} />
                <SortHeader label="Next inspection" sortKey="nextInsp" sort={sort} onSort={onSort} />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map(r => (
                <tr
                  key={`${r.source}-${r.id}`}
                  className="hover:bg-slate-50"
                  data-testid={`report-row-${r.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">
                    {r.id}
                    <div className="text-[10px] uppercase text-slate-400 tracking-wider mt-0.5">
                      {SOURCE_LABELS[r.source] || r.source}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{r.kind || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{r.customerName || <span className="text-slate-400">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{r.siteName || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{r.engineer || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(r.issuedAt)}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {r.nextInspectionDate ? (
                      <span className={
                        new Date(r.nextInspectionDate) < new Date()
                          ? 'text-red-700 font-semibold' : ''
                      }>{fmtDate(r.nextInspectionDate)}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      {(r.source === 'upload' && r.hasFile) || r.viewPath ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleView(r)}
                          title={r.source === 'upload' ? 'Open PDF' : 'Open in editor'}
                          data-testid={`view-report-${r.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {r.source === 'upload' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-700 hover:text-red-800 hover:bg-red-50"
                          onClick={() => handleDelete(r)}
                          title="Delete uploaded report"
                          data-testid={`delete-report-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => { setUploadOpen(false); refresh(); }}
      />
    </div>
  );
};

// -------- Upload dialog --------

const UploadDialog = ({ open, onClose, onUploaded }) => {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  // Cert-type registry sourced from `/api/admin/reports/types` — adding
  // to `CERT_TYPES` in `admin_reports_routes.py` instantly grows this
  // dropdown without a frontend change.
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    customerId: '', siteId: '', kind: 'EICR', title: '',
    engineer: '', issuedAt: '', nextInspectionDate: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    Promise.all([getUsers(), getSites(), getAdminReportTypes()])
      .then(([u, s, t]) => {
        setCustomers(u.filter(x => x.role === 'customer'));
        setSites(s);
        setTypes(t || []);
      })
      .catch(() => { /* non-fatal — form still works with typed ids */ });
  }, [open]);

  // Sites filtered to selected customer so the second dropdown is
  // scoped correctly. If the admin picks the site first, we
  // auto-fill customer from the site's owningCustomer.
  const scopedSites = useMemo(() => {
    if (!form.customerId) return sites;
    return sites.filter(s => s.customerId === form.customerId);
  }, [sites, form.customerId]);

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    setForm(f => ({
      ...f,
      siteId,
      customerId: f.customerId || site?.customerId || '',
    }));
  };

  const submit = async () => {
    setError(null);
    if (!file) { setError('Please pick a file to upload.'); return; }
    if (!form.customerId && !form.siteId) {
      setError('Please pick a customer or a site so this report is filed correctly.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
    setSaving(true);
    try {
      await uploadAdminReport(fd);
      toast.success('Report uploaded');
      // reset for next time
      setForm({ customerId: '', siteId: '', kind: 'EICR', title: '', engineer: '', issuedAt: '', nextInspectionDate: '' });
      setFile(null);
      onUploaded();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-report-dialog">
        <DialogHeader>
          <DialogTitle>Upload a report</DialogTitle>
          <DialogDescription>
            PDF or image. Bytes are stored in the database so the file
            survives redeploys. Max 20 MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Customer</Label>
            <Select
              value={form.customerId}
              onValueChange={(v) => setForm(f => ({ ...f, customerId: v, siteId: '' }))}
            >
              <SelectTrigger data-testid="upload-customer" className="mt-1.5">
                <SelectValue placeholder="Pick a customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.company || c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Site (optional)</Label>
            <Select value={form.siteId} onValueChange={handleSiteChange}>
              <SelectTrigger data-testid="upload-site" className="mt-1.5">
                <SelectValue placeholder="Pick a site" />
              </SelectTrigger>
              <SelectContent>
                {scopedSites.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Report type</Label>
              <Select
                value={form.kind}
                onValueChange={(v) => setForm(f => ({ ...f, kind: v }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="upload-kind">
                  <SelectValue placeholder="Pick a type" />
                </SelectTrigger>
                <SelectContent>
                  {types.map(t => (
                    <SelectItem key={t.label} value={t.label}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1">
                Reference will be minted as{' '}
                <span className="font-mono">
                  {(types.find(x => x.label === form.kind)?.prefix) || 'CERT'}-
                  {(form.issuedAt || '').replaceAll('-', '') || 'YYYYMMDD'}
                </span>
              </div>
            </div>
            <div>
              <Label>Engineer</Label>
              <Input
                className="mt-1.5"
                placeholder="Who wrote it"
                value={form.engineer}
                onChange={e => setForm(f => ({ ...f, engineer: e.target.value }))}
                data-testid="upload-engineer"
              />
            </div>
          </div>

          <div>
            <Label>Title</Label>
            <Input
              className="mt-1.5"
              placeholder="Optional — defaults to the report type"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="upload-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Issued date</Label>
              <Input
                className="mt-1.5"
                type="date"
                value={form.issuedAt}
                onChange={e => setForm(f => ({ ...f, issuedAt: e.target.value }))}
                data-testid="upload-issued"
              />
            </div>
            <div>
              <Label>Next inspection</Label>
              <Input
                className="mt-1.5"
                type="date"
                value={form.nextInspectionDate}
                onChange={e => setForm(f => ({ ...f, nextInspectionDate: e.target.value }))}
                data-testid="upload-next"
              />
            </div>
          </div>

          <div>
            <Label>File</Label>
            <Input
              className="mt-1.5"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file"
            />
            {file && (
              <div className="text-xs text-slate-500 mt-1">
                Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-md p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="upload-cancel">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="upload-submit"
          >
            {saving ? 'Uploading…' : (<><Upload className="h-4 w-4 mr-1" /> Upload</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminReports;
